import { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { MediaOwnerPermissions } from "@/hooks/useMediaOwnerRole";

type TabKey =
  | "locations" | "campaigns" | "revenue" | "analytics"
  | "activity" | "documents" | "team" | "support" | "profile";

interface TourStep {
  target: string;
  title: string;
  titleAr: string;
  description: string;
  descriptionAr: string;
  tab?: TabKey;
  targetTab?: TabKey;
}

type SpotlightRect = { top: number; left: number; width: number; height: number };

const STEPS: TourStep[] = [
  {
    target: "header",
    title: "Your Dashboard",
    titleAr: "لوحة التحكم",
    description: "This is your command center. It shows your company branding, your role (Owner, Manager, or Viewer), and gives quick access to notifications, the tour replay button, and logout.",
    descriptionAr: "هذا هو مركز التحكم الخاص بك. يعرض علامتك التجارية ودورك (مالك، مدير، أو مشاهد)، ويوفر وصولاً سريعاً للإشعارات وزر إعادة الجولة وتسجيل الخروج.",
  },
  {
    target: "stats",
    title: "Key Metrics",
    titleAr: "المقاييس الرئيسية",
    description: "Five key metrics at a glance: total locations in your network, total screens across all sites, number of approved running campaigns, locations flagged for maintenance, and total revenue earned from campaigns.",
    descriptionAr: "خمسة مقاييس رئيسية بنظرة واحدة: إجمالي المواقع في شبكتك، إجمالي الشاشات عبر جميع المواقع، عدد الحملات المعتمدة الجارية، المواقع التي تحتاج صيانة، وإجمالي الإيرادات المكتسبة من الحملات.",
  },
  {
    target: "sidebar",
    title: "Navigation",
    titleAr: "التنقل",
    description: "Navigate between all sections of your dashboard here. The tabs shown depend on your role — Viewers see fewer options than Owners. On mobile, tap the menu icon to open this as a slide-out panel.",
    descriptionAr: "تنقل بين جميع أقسام لوحة التحكم من هنا. علامات التبويب المعروضة تعتمد على دورك — المشاهدون يرون خيارات أقل من المالكين. على الهاتف، اضغط على أيقونة القائمة لفتح لوحة جانبية.",
  },
  {
    target: "mo-locations-panel",
    title: "Locations",
    titleAr: "المواقع",
    description: "Your full screen inventory. Add new locations, edit details like address, screen count, and pricing per screen. Set maintenance status with date ranges, toggle whether each location appears on the advertiser booking map, and export your location data.",
    descriptionAr: "مخزون شاشاتك الكامل. أضف مواقع جديدة، عدّل التفاصيل كالعنوان وعدد الشاشات والتسعير لكل شاشة. حدد حالة الصيانة مع نطاقات التواريخ، وتحكم في ظهور كل موقع على خريطة حجز المعلنين، وصدّر بيانات مواقعك.",
    tab: "locations",
  },
  {
    target: "mo-campaigns-panel",
    title: "Campaign Approvals",
    titleAr: "موافقات الحملات",
    description: "Incoming campaign requests from advertisers land here. Review budget, duration, and creative details, then approve or reject with one click. The badge on this tab updates in real-time so you never miss a pending request.",
    descriptionAr: "طلبات الحملات الواردة من المعلنين تظهر هنا. راجع الميزانية والمدة وتفاصيل التصميم، ثم وافق أو ارفض بنقرة واحدة. شارة هذا التبويب تتحدث تلقائياً حتى لا يفوتك أي طلب معلق.",
    tab: "campaigns",
  },
  {
    target: "mo-revenue-panel",
    title: "Revenue Overview",
    titleAr: "نظرة عامة على الإيرادات",
    description: "Track your earnings from approved campaigns. See a monthly revenue breakdown chart, identify your highest-earning periods, and monitor revenue trends over time to inform your pricing strategy.",
    descriptionAr: "تتبع أرباحك من الحملات المعتمدة. شاهد مخطط تفصيلي للإيرادات الشهرية، حدد فترات الأرباح الأعلى، وراقب اتجاهات الإيرادات بمرور الوقت لتحسين استراتيجية التسعير.",
    tab: "revenue",
  },
  {
    target: "mo-analytics-panel",
    title: "Analytics",
    titleAr: "التحليلات",
    description: "Performance insights for your network. View screen utilization rates, campaign performance metrics, and occupancy trends to help you optimize pricing and availability across your locations.",
    descriptionAr: "رؤى أداء شبكتك. اعرض معدلات استخدام الشاشات ومقاييس أداء الحملات واتجاهات الإشغال لمساعدتك في تحسين التسعير والتوفر عبر مواقعك.",
    tab: "analytics",
  },
  {
    target: "mo-activity-panel",
    title: "Activity Feed",
    titleAr: "سجل النشاط",
    description: "A chronological audit log of everything happening in your network — new campaign submissions, approvals, rejections, and status changes. Use the filters to quickly find specific events.",
    descriptionAr: "سجل تدقيق زمني لكل ما يحدث في شبكتك — تقديمات حملات جديدة، موافقات، رفض، وتغييرات الحالة. استخدم المرشحات للعثور بسرعة على أحداث محددة.",
    tab: "activity",
  },
  {
    target: "mo-documents-panel",
    title: "Documents",
    titleAr: "الوثائق",
    description: "Store and manage all your important paperwork in one place. Upload contracts, invoices, permits, and certificates. Organize by type, download when needed, or remove outdated files.",
    descriptionAr: "خزّن وأدر جميع أوراقك المهمة في مكان واحد. ارفع العقود والفواتير والتصاريح والشهادات. نظّم حسب النوع، حمّل عند الحاجة، أو احذف الملفات القديمة.",
    tab: "documents",
  },
  {
    target: "mo-team-panel",
    title: "Team Management",
    titleAr: "إدارة الفريق",
    description: "Invite colleagues by email and assign roles: Owner has full control, Manager can approve campaigns and edit locations, Viewer has read-only access. The primary owner account cannot be removed.",
    descriptionAr: "ادعُ زملاءك بالبريد الإلكتروني وعيّن الأدوار: المالك لديه تحكم كامل، المدير يمكنه الموافقة على الحملات وتعديل المواقع، المشاهد لديه صلاحية القراءة فقط. حساب المالك الأساسي لا يمكن إزالته.",
    tab: "team",
  },
  {
    target: "mo-support-panel",
    title: "Support",
    titleAr: "الدعم",
    description: "Need help? Submit a support ticket describing your issue or question. Track the status of your requests and receive updates when they are resolved.",
    descriptionAr: "تحتاج مساعدة؟ أرسل تذكرة دعم تصف مشكلتك أو سؤالك. تتبع حالة طلباتك واستلم التحديثات عند حلها.",
    tab: "support",
  },
  {
    target: "mo-profile-panel",
    title: "Profile & Settings",
    titleAr: "الملف الشخصي والإعدادات",
    description: "View and update your account settings. Change your display name, review your assigned role, and use the Security section to reset your password. This is also where you can see which organization you belong to.",
    descriptionAr: "عرض وتحديث إعدادات حسابك. غيّر اسم العرض، راجع دورك المعيّن، واستخدم قسم الأمان لإعادة تعيين كلمة المرور. هنا أيضاً يمكنك معرفة المؤسسة التي تنتمي إليها.",
    tab: "profile",
  },
  {
    target: "notifications",
    title: "Notifications",
    titleAr: "الإشعارات",
    description: "Real-time alerts for new campaign requests, approval status changes, and maintenance reminders. Click any notification to jump directly to the relevant section of your dashboard.",
    descriptionAr: "تنبيهات فورية لطلبات الحملات الجديدة وتغييرات حالة الموافقة وتذكيرات الصيانة. انقر على أي إشعار للانتقال مباشرة إلى القسم المعني في لوحة التحكم.",
  },
];

const EASE = "cubic-bezier(0.4, 0, 0.2, 1)";
const TRANSITION_MS = 500;

interface DashboardTourProps {
  isOpen: boolean;
  onClose: () => void;
  switchTab: (tab: TabKey) => void;
  language: string;
  permissions: MediaOwnerPermissions;
}

const TAB_PERMISSION_MAP: Partial<Record<TabKey, keyof MediaOwnerPermissions>> = {
  revenue: "canViewRevenue",
  analytics: "canViewAnalytics",
  activity: "canViewActivity",
  team: "canManageTeam",
  support: "canSubmitSupport",
};

export const DashboardTour = ({ isOpen, onClose, switchTab, language, permissions }: DashboardTourProps) => {
  const { canViewRevenue, canViewAnalytics, canViewActivity, canManageTeam, canSubmitSupport } = permissions;

  const filteredSteps = useMemo(() => {
    return STEPS.filter(s => {
      if (!s.tab) return true;
      const permKey = TAB_PERMISSION_MAP[s.tab];
      if (!permKey) return true; // tabs without permission mapping are always shown
      const permissionValues: Record<keyof Pick<MediaOwnerPermissions, "canViewRevenue" | "canViewAnalytics" | "canViewActivity" | "canManageTeam" | "canSubmitSupport">, boolean> = {
        canViewRevenue,
        canViewAnalytics,
        canViewActivity,
        canManageTeam,
        canSubmitSupport,
      };
      return !!permissionValues[permKey];
    });
  }, [
    canViewRevenue,
    canViewAnalytics,
    canViewActivity,
    canManageTeam,
    canSubmitSupport,
  ]);

  const [currentStep, setCurrentStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 16, left: 16 });
  const [mounted, setMounted] = useState(false);
  const [activeTabSwitch, setActiveTabSwitch] = useState<TabKey | null>(null);
  const pendingStepRef = useRef(0);
  const currentStepRef = useRef(0);
  const measureTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const recalcTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const rafRef = useRef<number>();
  const observerRef = useRef<ResizeObserver | null>(null);
  const tourOpenRef = useRef(false);

  const isAr = language === "ar";
  const step = filteredSteps[currentStep];
  const pad = 8;

  const positionTooltip = useCallback((rect: SpotlightRect | null) => {
    const tooltipW = Math.min(380, window.innerWidth - 32);
    const tooltipH = 230;
    const gap = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const clampLeft = (value: number) => Math.max(gap, Math.min(value, vw - tooltipW - gap));
    const clampTop = (value: number) => Math.max(gap, Math.min(value, vh - tooltipH - gap));

    if (!rect) {
      return { top: clampTop(vh / 2 - tooltipH / 2), left: clampLeft(vw / 2 - tooltipW / 2) };
    }

    // Try bottom
    if (rect.top + rect.height + gap + tooltipH < vh) {
      return { top: rect.top + rect.height + gap, left: clampLeft(rect.left + rect.width / 2 - tooltipW / 2) };
    }
    // Try top
    if (rect.top - gap - tooltipH > 0) {
      return { top: rect.top - gap - tooltipH, left: clampLeft(rect.left + rect.width / 2 - tooltipW / 2) };
    }
    // Try right
    if (rect.left + rect.width + gap + tooltipW < vw) {
      return { top: clampTop(rect.top + rect.height / 2 - tooltipH / 2), left: rect.left + rect.width + gap };
    }
    // Fallback left
    return { top: clampTop(rect.top + rect.height / 2 - tooltipH / 2), left: clampLeft(rect.left - tooltipW - gap) };
  }, []);

  const clearScheduledWork = useCallback(() => {
    clearTimeout(measureTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const measureAndHighlight = useCallback((stepIndex: number, allowScroll = true) => {
    const s = filteredSteps[stepIndex];
    if (!s) return;

    clearScheduledWork();

    const getTarget = () => {
      const el = document.querySelector(`[data-tour="${s.target}"]`) as HTMLElement | null;
      if (!el) return null;
      return el;
    };

    const toSpotlightRect = (r: DOMRect) => {
      // Use raw element bounds with padding — do NOT clamp to viewport, otherwise
      // tall panels get a viewport-sized spotlight that no longer points at anything.
      const top = r.top - pad;
      const left = Math.max(4, r.left - pad);
      const width = Math.max(0, r.width + pad * 2);
      const height = Math.max(0, r.height + pad * 2);
      if (width < 8 || height < 8) return null;
      return { top, left, width, height };
    };

    const doMeasure = (el: HTMLElement) => {
      const r = el.getBoundingClientRect();
      const rect = toSpotlightRect(r);
      if (!rect) return false;
      setSpotlightRect(rect);
      setTooltipPos(positionTooltip(rect));
      return true;
    };

    let didScroll = false;
    let attempts = 0;
    const attemptMeasure = () => {
      const el = getTarget();
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          // Scroll into view when element is fully off, or its top is above viewport,
          // or it starts so low that the tooltip wouldn't fit below it.
          const topAbove = r.top < 16;
          const tooLow = r.top > window.innerHeight - 120;
          const fullyOut = r.bottom < 24 || r.top > window.innerHeight - 24;
          if (allowScroll && !didScroll && (fullyOut || topAbove || tooLow)) {
            didScroll = true;
            el.scrollIntoView({ behavior: "auto", block: "start", inline: "nearest" });
            // Nudge up so the highlighted top edge isn't flush against the viewport edge
            window.scrollBy({ top: -24, left: 0, behavior: "auto" });
            measureTimerRef.current = setTimeout(attemptMeasure, 120);
            return;
          }
          if (!doMeasure(el)) {
            measureTimerRef.current = setTimeout(attemptMeasure, 100);
            return;
          }
          try {
            observerRef.current = new ResizeObserver(() => doMeasure(el));
            observerRef.current.observe(el);
          } catch {
            observerRef.current = null;
          }
          return;
        }
      }
      if (attempts++ < 30) {
        measureTimerRef.current = setTimeout(attemptMeasure, 100);
      } else {
        setSpotlightRect(null);
        setTooltipPos(positionTooltip(null));
      }
    };
    const delay = s.tab ? 220 : 40;
    rafRef.current = requestAnimationFrame(() => {
      measureTimerRef.current = setTimeout(attemptMeasure, delay);
    });
  }, [positionTooltip, filteredSteps, clearScheduledWork]);

  const goToStep = useCallback((stepIndex: number) => {
    const clamped = Math.max(0, Math.min(stepIndex, filteredSteps.length - 1));
    const s = filteredSteps[clamped];
    if (!s) return;
    currentStepRef.current = clamped;
    pendingStepRef.current = clamped;
    setCurrentStep(clamped);
    setActiveTabSwitch(s.tab ?? null);
    if (s.tab) switchTab(s.tab);
    else measureAndHighlight(clamped);
  }, [filteredSteps, switchTab, measureAndHighlight]);

  useLayoutEffect(() => {
    if (!isOpen || !activeTabSwitch) return;
    measureAndHighlight(pendingStepRef.current);
    setActiveTabSwitch(null);
  }, [isOpen, activeTabSwitch, measureAndHighlight]);

  useEffect(() => {
    return () => {
      clearScheduledWork();
      clearTimeout(recalcTimerRef.current);
    };
  }, [clearScheduledWork]);

  useEffect(() => {
    if (!isOpen) {
      tourOpenRef.current = false;
      setMounted(false);
      setSpotlightRect(null);
      setActiveTabSwitch(null);
      clearScheduledWork();
      return;
    }

    if (tourOpenRef.current) return;
    tourOpenRef.current = true;
    setMounted(true);
    goToStep(0);
  }, [isOpen, goToStep, clearScheduledWork]);

  // Resize recalc only — spotlight stays fixed in viewport on scroll
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => {
      clearTimeout(recalcTimerRef.current);
      recalcTimerRef.current = setTimeout(() => measureAndHighlight(currentStepRef.current, false), 80);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
      clearTimeout(recalcTimerRef.current);
    };
  }, [isOpen, measureAndHighlight]);

  // Lock page scroll while tour is open so spotlight never drifts off target
  useEffect(() => {
    if (!isOpen) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [isOpen]);

  // Cleanup observer/measure/raf when closing
  useEffect(() => {
    if (isOpen) return;
    observerRef.current?.disconnect();
    observerRef.current = null;
    clearTimeout(measureTimerRef.current);
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, [isOpen]);

  // Keyboard nav
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight" || e.key === "ArrowDown") goNext();
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") goBack();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  });

  const goNext = useCallback(() => {
    if (currentStep >= filteredSteps.length - 1) { onClose(); return; }
    const next = currentStep + 1;
    goToStep(next);
  }, [currentStep, onClose, goToStep, filteredSteps]);

  const goBack = useCallback(() => {
    if (currentStep <= 0) return;
    const prev = currentStep - 1;
    goToStep(prev);
  }, [currentStep, goToStep]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000]"
      style={{ opacity: mounted ? 1 : 0, transition: `opacity 0.3s ${EASE}`, touchAction: 'none', overscrollBehavior: 'none' }}
      onTouchMove={(e) => e.preventDefault()}
    >
      {/* Click-away backdrop (invisible, behind spotlight) */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* CSS box-shadow spotlight */}
      {spotlightRect && (
        <div
          className="absolute pointer-events-none rounded-2xl"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            boxShadow: `0 0 0 9999px rgba(0,0,0,0.65), 0 0 0 2px hsl(var(--primary) / 0.6), 0 0 30px hsl(var(--primary) / 0.1)`,
            transition: `top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}, width ${TRANSITION_MS}ms ${EASE}, height ${TRANSITION_MS}ms ${EASE}`,
          }}
        />
      )}

      {/* Fallback full overlay when no element found */}
      {!spotlightRect && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ backgroundColor: "rgba(0,0,0,0.65)" }}
          onClick={onClose}
        />
      )}

      {/* Tooltip card — slides between positions */}
      <div
        className="absolute w-[380px] max-w-[calc(100vw-32px)] bg-card border border-border/60 rounded-2xl shadow-2xl overflow-hidden"
        style={{
          top: tooltipPos.top,
          left: tooltipPos.left,
          zIndex: 10001,
          transition: `top ${TRANSITION_MS}ms ${EASE}, left ${TRANSITION_MS}ms ${EASE}`,
        }}
      >
        {/* Gradient accent bar */}
        <div className="h-1 bg-gradient-to-r from-primary via-primary/60 to-transparent" />

        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              {/* Content fades on step change */}
              <h3
                key={`title-${currentStep}`}
                className="text-base font-bold text-foreground animate-fade-in"
              >
                {isAr ? step.titleAr : step.title}
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Description */}
          <p
            key={`desc-${currentStep}`}
            className={`text-sm text-muted-foreground leading-relaxed mb-4 animate-fade-in ${isAr ? "text-right" : ""}`}
          >
            {isAr ? step.descriptionAr : step.description}
          </p>

          {/* Footer: step dots + nav */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              {filteredSteps.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full"
                  style={{
                    height: 6,
                    width: i === currentStep ? 16 : 6,
                    backgroundColor: i === currentStep
                      ? "hsl(var(--primary))"
                      : i < currentStep
                        ? "hsl(var(--primary) / 0.4)"
                        : "hsl(var(--border))",
                    transition: `width 0.3s ${EASE}, background-color 0.3s ${EASE}`,
                  }}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={onClose}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
              >
                {isAr ? "تخطي" : "Skip"}
              </button>

              {currentStep > 0 && (
                <Button variant="outline" size="sm" onClick={goBack} className="rounded-xl h-8 px-3 text-xs">
                  <ChevronLeft className="h-3.5 w-3.5 mr-0.5" />
                  {isAr ? "السابق" : "Back"}
                </Button>
              )}

              <Button size="sm" onClick={goNext} className="rounded-xl h-8 px-3 text-xs bg-primary hover:bg-primary/90">
                {currentStep >= filteredSteps.length - 1
                  ? (isAr ? "إنهاء" : "Finish")
                  : (isAr ? "التالي" : "Next")}
                {currentStep < filteredSteps.length - 1 && <ChevronRight className="h-3.5 w-3.5 ml-0.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
